/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import NewsletterCapture from "@/components/NewsletterCapture";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const Insights = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPosts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/blog/posts`);
      setPosts(r.data);
    } catch (e) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  return (
    <>
      <section className="pt-[160px] pb-20 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-6">
          [ Insights & journal ]
        </div>
        <h1 className="font-display font-bold text-[3rem] md:text-[5rem] leading-[0.98] tracking-tightest max-w-5xl">
          Notes from the
          <span className="text-brand"> plant room.</span>
        </h1>
        <p className="mt-8 text-[18px] text-muted-foreground max-w-2xl leading-relaxed">
          Practical takes on indoor air quality, compliance, refrigerant phase-out and the
          unglamorous engineering details that keep buildings running.
        </p>
      </section>

      <section className="pb-24 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        {loading && (
          <div className="font-mono text-[12px] text-muted py-12" data-testid="insights-loading">
            Loading articles…
          </div>
        )}
        {error && (
          <div className="font-mono text-[12px] text-accent py-12" data-testid="insights-error">
            {error}
          </div>
        )}
        {!loading && !error && (
          <div className="divide-y divide-black/8">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={i * 60}>
                <Link
                  to={`/insights/${p.slug}`}
                  data-testid={`insight-${p.slug}`}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-6 py-10 hover:bg-paper-warm transition-colors -mx-6 px-6"
                >
                  <div className="md:col-span-3">
                    <div className="aspect-[4/3] rounded-sm overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-7">
                    <div className="flex items-center gap-3 mb-3 font-mono text-[11px] tracking-overline uppercase text-muted">
                      <span>{p.category}</span>
                      <span className="w-1 h-1 bg-muted rounded-full" />
                      <span>{formatDate(p.published_at)}</span>
                      <span className="w-1 h-1 bg-muted rounded-full" />
                      <span>{p.read_minutes} min read</span>
                    </div>
                    <h2 className="font-display font-bold text-[1.6rem] md:text-[2rem] tracking-tightest leading-tight mb-3 group-hover:text-brand transition-colors">
                      {p.title}
                    </h2>
                    <p className="text-[15px] text-muted-foreground leading-relaxed max-w-2xl">
                      {p.excerpt}
                    </p>
                  </div>
                  <div className="md:col-span-2 flex md:justify-end items-start">
                    <ArrowUpRight className="h-6 w-6 text-ink/30 group-hover:text-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <NewsletterCapture source="insights-list" />
    </>
  );
};

export default Insights;
